import GD from '../GD'
import { WSErrors } from '../enums/wsErrors'
import { LocalStorage, LS } from '../utils'
import { WSMessage } from '../enums'

class WS {
  private ls: LocalStorage = new LocalStorage()
  private ws: WebSocket | null = null
  private key: string | null = null
  private profile: IProfile | null = null
  private url: string | null = null
  private delayedPackets: DelayedPacket[] = []
  private wsConnectRetryTimeout: ReturnType<typeof setTimeout> | null = null
  private realizedDelayedMethods: string[] = [WSMessage.MESSAGE_ADD, WSMessage.CHAT_USER_ENTER]
  private isManualClose: boolean

  constructor() {
    GD.AUTH_COMPLETE.subscribe(async data => {
      this.url = window.DCC_CONFIG.WS_URL!
      this.key = data.key
      this.profile = data.profile
      this.connect()
    })

    GD.REQ_WS_STATUS.listener = async () => {
      return (this.ws) ? this.ws.readyState : 3
    }

    GD.WS_SEND.subscribe(async packet => {
      // Handle network-offline mode
      if (!window.navigator.onLine) {
        console.warn('Network is offline. Can\'t send WebSocket packet.')
        return await this.addToDelayedPacketsList(packet)
      }

      // Handle WS-offline  mode
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        if (packet.method === 'msgAdd') {
          return await this.sendMsgViaPHP(packet)
        }
        await this.addToDelayedPacketsList(packet)
        return console.warn('Can\'t send packet, no active connection: ', packet)
      }
      // If network estabilished -
      if (this.delayedPackets.length > 0) this.launchDelayedPackets()

      if (packet.method !== 'pong') {
        let log = '\t' + this.url
        log += this.generateDebug(packet)
        GD.LOG.invoke(log)
      }
      this.ws.send(JSON.stringify(packet))
    })

    GD.SERVICE_READY.invoke('ws')
  }

  private clearLSDelayedMsgsPackets() {
    this.ls.set(LS.UNSENT_MESSAGES, [])
  }

  private async addToDelayedPacketsList(packet: DelayedPacket) {
    if (!packet) return

    if (!this.realizedDelayedMethods.includes(packet.method)) {
      this.delayedPackets.push(packet)
      console.warn(this.delayedPackets)
      return
    }

    if (packet.method === 'msgAdd') {
      const profile = await GD.GET_PROFILE.request()
      if (!profile) return

      this.delayedPackets.push(packet)
      this.sendMsgOffline(packet, profile)
      const msgAddPackets = this.delayedPackets.filter(packet => packet.method === 'msgAdd')

      // На случай если связь не будет восстановлена
      this.ls.set(LS.UNSENT_MESSAGES, msgAddPackets)
    }

    if (packet.method === WSMessage.CHAT_USER_ENTER) {
      const existingUserEnterPacketIndex = this.delayedPackets.findIndex(
        (p: DelayedPacket) => p.method === WSMessage.CHAT_USER_ENTER
      )
      if (existingUserEnterPacketIndex !== -1) {
        // Оставить один метод, так как открыт то один чат
        this.delayedPackets[existingUserEnterPacketIndex] = packet
      }
      else {
        this.delayedPackets.push(packet)
      }
    }
  }

  /*/**
     * Send message for user
     *
     * @see CONNECT-19449
     *
     * @param string $chatUID
     * @param string $txt
     * @param int $mid
     *
     * @param string $type optional
     * @param bool $anonymous false by default
     * @param bool $pushable false by default
     * @param string $pushBody optional
     * @param string $pushTitle optional
     * @param int $pointID optional
     * @return mixed|null
    
    api.wsSend(string $chatUID, string $txt, int $mid,
    string $type = '', bool $anonymous = false,
    bool $pushable = false, string $pushBody = '', string $pushTitle = '',
    int $pointID = 0
    )*/
  private async sendMsgViaPHP(packet: { method: string, data: IObjectData | null }) {
    const req: APIRequest = {
      method: 'api.wsSend',
      pushable: true
    }

    if (packet.data != null && typeof packet.data === 'object') {
      for (const i in packet.data)
        req[i] = packet.data[i]
    }
    else return

    const profile = await GD.GET_PROFILE.request()
    if (!profile) return

    this.sendMsgOffline(packet, profile)

    const resp = await GD.REQ_HTTP.request(req)

    if (resp.error) {
      console.error('Can\'t send msg via HTTP, ' + resp.errorMsg)
      this.addToDelayedPacketsList(packet)
      return
    }
    // Это пиздец. не забыть потроллить серверменов
    let phpData = resp.data as IObjectData
    for (let i = 0; i < 2; i++) {
      if (phpData && typeof phpData === 'object' && 'data' in phpData && typeof phpData.data === 'object')
        phpData = phpData.data
    }

    if (phpData && 'error' in phpData && phpData.error) {
      console.error('Can\'t send msg via HTTP, WS ERR: ')
      return
    }
    if (phpData && 'msgData' in phpData && typeof phpData.msgData === 'object') {
      this.parsePacket(JSON.stringify({
        method: WSMessage.MESSAGE_ADD,
        data: {
          anonymous: false,
          chatUID: packet.data.chatUID,
          chat_uid: packet.data.chatUID,
          created: Math.round((+new Date() / 1000)),
          delivery: 'sent',
          id: phpData.msgData.id,
          mid: '',
          num: phpData.msgData.num,
          platform: 'desktop',
          stat: [],
          status: 'created',
          text: packet.data.text,
          user_avatar: profile.avatar,
          user_name: profile.name,
          user_uid: profile.uid
        }
      }))

      GD.WS_MSG_SENT_VIA_HTTP.invoke({
        num: phpData?.msgData?.num || 0,
        id: phpData?.msgData?.id || 0,
        text: packet.data.text,
        chatUID: packet.data.chatUID
      })

      return
    }

    GD.WS_MSG_SENT_VIA_HTTP.invoke({
      num: phpData?.num || 0,
      id: phpData?.id || 0,
      text: packet.data.text,
      chatUID: packet.data.chatUID
    })
  }

  private generateDebug(data: object, lvl = 1): string {
    let str = ''
    let stp = ''
    for (let n = 0; n < lvl; n++)
      stp += '\t'
    for (const i in data) {
      let itm = data[i]
      if (itm === null || itm === undefined)
        str += '\n' + stp + i + ': null'
      else if (typeof itm === 'object') {
        str += '\n' + stp + i + ':' + this.generateDebug(itm, ++lvl)
      }
      else {
        if (typeof itm === 'string' && itm.length > 60)
          itm = itm.substr(0, 50) + '... total(' + itm.length + ')'
        str += '\n' + stp + i + ': ' + itm
      }
    }
    return str
  }

  private sendMsgOffline(packet: { method: string, data: IObjectData | null }, profile: IProfile) {
    if (!packet.data) return

    this.parsePacket(JSON.stringify({
      method: WSMessage.MESSAGE_ADD_OFFLINE,
      data: {
        anonymous: false,
        chatUID: packet.data.chatUID,
        chat_uid: packet.data.chatUID,
        created: Math.round((+new Date() / 1000)),
        delivery: 'sent',
        id: '',
        mid: '-1',
        num: Infinity,
        platform: 'desktop',
        stat: [],
        status: 'error',
        text: packet.data.text,
        user_avatar: profile.avatar,
        user_name: profile.name,
        user_uid: profile.uid
      }
    }))
  }

  private connect(): void {
    this.close()

    if (this.url == null || !this.key) return

    GD.LOG.invoke('WS Connecting to: ' + this.url)

    this.ws = new WebSocket(this.url)

    this.ws.onopen = () => {
      GD.LOG.invoke('WS opened')
      if (this.key == null) {
        this.close()
        return
      }
      if (this.profile?.type === 'guest') {
        GD.WS_SEND.invoke({
          method: 'auth',
          data: {
            authKey: this.key,
            guestName: this.profile.name,
            guestUID: this.profile.uid
          }
        })
      }
      else {
        GD.WS_SEND.invoke({
          method: 'auth',
          data: {
            authKey: this.key
          }
        })
      }
    }

    this.ws.onclose = (e: CloseEvent) => {
      console.log({ e })
      GD.WS_CLOSED.invoke()
      GD.LOG.invoke('WS closed')
      this.delayedPackets = []

      if (this.isManualClose) return

      this.wsConnectRetryTimeout = setTimeout(() => {
        GD.LOG.invoke('WS auto-reconnect...')
        this.connect()
      }, 3000)
    }

    this.ws.onerror = (e: Event) => {
      GD.LOGE.invoke(['WS error', e])
    }

    this.ws.onmessage = (e: MessageEvent) => {
      if (typeof e.data !== 'string') {
        return
      }
      if (e.data.includes('"error":true')) {
        this.processWsError(e.data)
      }

      const packetString = e.data

      if (packetString.startsWith('{"method":"ping",')) {
        GD.WS_SEND.invoke({
          method: 'pong',
          data: null
        })
        return
      }

      this.parsePacket(packetString)
    }
  }

  private processWsError(packet: string) {
    console.error('WS error: ', packet)

    const { data: { error = false, info = '' }, method } = JSON.parse(packet)

    if (method !== 'auth' && !error) return

    if (info.includes('Too many attempts')) {
      if (this.wsConnectRetryTimeout) clearTimeout(this.wsConnectRetryTimeout)
      if (this.ws) this.ws.onclose = null
      GD.LOGOUT.invoke()
    }
  }

  private parsePacket(packetString: string) {
    let packet: { method: string, data: IObjectData } | null = null

    try {
      packet = JSON.parse(packetString)
    }
    catch (e) {
      console.error(e)
    }

    if (packet === null) {
      console.error('Wrong packet from server')
      return
    }

    if (!('method' in packet) || !('data' in packet)) {
      console.error('Wrong packet structure')
      return
    }

    if (packet.data.error) {
      return this.handleError(packet.data.reason || packet.data.info)
    }

    if (packet.method === 'auth') {
      GD.LOG.invoke('WS authorized on: ' + this.url)
      GD.WS_AUTHORIZED.invoke(this.profile?.type)
      return
    }

    if (packet.method === 'init') {
      if (packet.data && !packet.data.error) {
        GD.WS_AUTHORIZED.invoke(this.profile?.type)
        this.launchDelayedPackets()
      }
      return
    }

    GD.WS_MSG.invoke(packet)
  }

  private launchDelayedPackets() {
    if (this.delayedPackets.length > 0) {
      GD.INVOKE_DELAYED_EVENTS.invoke(this.delayedPackets)
      this.delayedPackets = []
      this.clearLSDelayedMsgsPackets()
    }
  }

  private close(manualClose = false): void {
    if (this.ws) {
      this.ws.onclose = null
      this.ws.onerror = null
      this.ws.onmessage = null
      this.ws.onopen = null
      this.ws.close()
    }

    this.ws = null
    this.isManualClose = manualClose
  }

  private handleError(error: string): void {
    switch (error) {
      case (WSErrors.UNAUTHORIZED):
      case (WSErrors.TO_MANY_REQUESTS): return this.close(true)
    }
  }
}

export default WS